import { FC } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { Address } from '@weave/schema-gen-ts/dist/schemas/phone/address/v1/address_service.pb';
import { stateOptions, CountryCodes, countryOptions } from '@frontend/geography';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  ContentLoader,
  DropdownField,
  FormRow,
  Modal,
  PostalCodeField,
  TextField,
  Text,
} from '@frontend/design-system';
import { useAddressForm } from './use-e911-address-form';

interface Props {
  address?: Address;
  onBack?: () => void;
  onCancel?: () => void;
  onSave: (address: Address) => void;
  isForm?: boolean;
  isLoading?: boolean;
  footerStyles?: Interpolation<Theme>;
}

export const E911AddressForm: FC<React.PropsWithChildren<Props>> = ({
  address,
  onBack,
  onCancel,
  onSave,
  isForm = true,
  isLoading,
  footerStyles,
}) => {
  const handleSave = () => {
    onSave({ id: address?.id, ...values } as Address);
  };

  const { getFieldProps, formProps, getErrors, values } = useAddressForm({
    address,
    onSubmit: handleSave,
  });

  const props = {
    onBack,
    onCancel,
    getErrors,
    getFieldProps,
    isForm,
    footerStyles,
  };

  const Container = isForm ? (
    <form css={formStyles} {...formProps}>
      <E911FormFields {...props} />
    </form>
  ) : (
    <div css={formStyles}>
      <E911FormFields onSave={handleSave} {...props} />
    </div>
  );

  return (
    <>
      <ContentLoader show={isLoading} />
      {Container}
    </>
  );
};

const E911FormFields = ({
  getFieldProps,
  getErrors,
  onBack,
  onCancel,
  onSave,
  isForm,
  footerStyles,
}: {
  getErrors: ReturnType<typeof useAddressForm>['getErrors'];
  getFieldProps: ReturnType<typeof useAddressForm>['getFieldProps'];
  onBack?: () => void;
  onCancel?: () => void;
  onSave?: (address?: Address) => void;
  isForm: boolean;
  footerStyles?: Interpolation<Theme>;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'e911' });
  const countryProps = getFieldProps('country');

  return (
    <>
      <FormRow
        css={css`
          margin-top: ${theme.spacing(1)};
        `}
      >
        <TextField {...getFieldProps('name')} label={t('Address Name')} />
      </FormRow>
      <FormRow>
        <DropdownField {...getFieldProps('country')} label={t('Country')}>
          {countryOptions.map(({ label, value }) => (
            <DropdownField.Option key={value} value={value}>
              {label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </FormRow>
      <FormRow>
        <TextField {...getFieldProps('street')} label={t('Street Address')} />
      </FormRow>
      <FormRow>
        <TextField {...getFieldProps('streetSecondary')} label={t('Address Line 2')} />
      </FormRow>
      <FormRow>
        <TextField {...getFieldProps('city')} label={t('City')} />
      </FormRow>
      <FormRow>
        <DropdownField
          {...getFieldProps('state')}
          label={countryProps.value === CountryCodes.USA ? t('State') : t('Province')}
        >
          {stateOptions[countryProps.value as CountryCodes]?.map(({ label, value }) => (
            <DropdownField.Option key={value} value={value} searchValue={label}>
              {label}
            </DropdownField.Option>
          ))}
        </DropdownField>
        <PostalCodeField {...getFieldProps('postalCode')} label={t('Postal Code')} />
      </FormRow>
      <Modal.Footer
        css={footerStyles}
        primary={{
          label: t('Save'),
          onClick: () => {
            isForm ? 'submit' : onSave?.();
          },
          disabled: Object.entries(getErrors()).length > 0,
        }}
        secondary={{
          label: t('Cancel'),
          onClick: onCancel,
        }}
        tertiary={{
          label: onBack && (
            <div css={{ display: 'flex', gap: theme.spacing(1) }}>
              <Icon name='back' />
              <Text color='primary' weight='bold'>
                {t('Back')}
              </Text>
            </div>
          ),
          onClick: onBack && onBack,
        }}
      />
    </>
  );
};

const formStyles = css`
  & > div:last-of-type {
    padding: 0;
  }
`;
